import { FC, useRef, useState } from 'react';
import clsx from 'clsx';
import { Typography } from '@mui/material';
import { TaskItem } from '@demind-inc/core';
import { AddOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { SubTasks, AddSubTask } from '../../KanbanView';
import type { AddSubTaskRef } from '../../KanbanView/SubTasks/AddSubTask/AddSubTask';
import { AddTodoTaskItemProps, TodoTaskUpdate } from '../../../data-access';
import './TaskSubtasksSection.scss';

interface TaskSubtasksSectionProps {
  parentTask?: TaskItem;
  subtasks: TaskItem[];
  canEdit: boolean;
  onCompleteSubTask: (taskId: string, completed: boolean) => void;
  onUpdateSubTask: (taskId: string, updates: TodoTaskUpdate) => void;
  onCreateSubTask: (options: AddTodoTaskItemProps) => Promise<void>;
  onDeleteSubTask: (taskId: string) => void;
  onReorderSubTasks?: (sourceId: string, targetId: string, position: 'before' | 'after') => void;
  className?: string;
  updatingSubTaskId?: string | null;
}

export const TaskSubtasksSection: FC<TaskSubtasksSectionProps> = ({
  parentTask,
  subtasks,
  canEdit,
  onCompleteSubTask,
  onUpdateSubTask,
  onCreateSubTask,
  onDeleteSubTask,
  onReorderSubTasks,
  className,
  updatingSubTaskId,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const addSubTaskRef = useRef<AddSubTaskRef | null>(null);
  const hasSubtasks = subtasks.length > 0;
  const [isEditingSubtask, setIsEditingSubtask] = useState(false);

  const handleAddShortcut = () => {
    addSubTaskRef.current?.startCreating();
  };

  return (
    <div ref={containerRef} className={clsx('task-subtasks-section', className)}>
      <div className="task-subtasks-section__header">
        {(hasSubtasks || isEditingSubtask) && (
          <Typography variant="subtitle2" className="task-subtasks-section__title">
            {t('task.subtasks.titleWithCount', { count: subtasks.length })}
          </Typography>
        )}

        {canEdit && (hasSubtasks || isEditingSubtask) && (
          <button
            type="button"
            className="task-subtasks-section__action"
            onClick={handleAddShortcut}
          >
            <span>{t('task.subtasks.addSubtask')}</span>
            <AddOutlined className="task-subtasks-section__action__icon" />
          </button>
        )}
      </div>

      {parentTask && (
        <>
          {hasSubtasks ? (
            <>
              <SubTasks
                parentTask={parentTask}
                subtasks={subtasks}
                canEdit={canEdit}
                onCompleteSubTask={onCompleteSubTask}
                onUpdateSubTask={onUpdateSubTask}
                onCreateSubTask={onCreateSubTask}
                onDeleteSubTask={onDeleteSubTask}
                onReorderSubTasks={onReorderSubTasks}
                className="task-subtasks-section__list"
                hideAddButton={true}
                updatingSubTaskId={updatingSubTaskId}
              />
              {canEdit && (
                <div
                  className={clsx('task-subtasks-section__add-container', {
                    'task-subtasks-section__add-container--hidden': !isEditingSubtask,
                  })}
                >
                  <AddSubTask
                    ref={addSubTaskRef}
                    parentTask={parentTask}
                    subtasksCount={subtasks.length}
                    onCreateSubTask={onCreateSubTask}
                    onEditStateChange={setIsEditingSubtask}
                  />
                </div>
              )}
            </>
          ) : (
            canEdit && (
              <div className="task-subtasks-section__empty">
                <AddSubTask
                  ref={addSubTaskRef}
                  parentTask={parentTask}
                  subtasksCount={0}
                  onCreateSubTask={onCreateSubTask}
                  onEditStateChange={setIsEditingSubtask}
                />
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};
